import * as React from "react";
import { view } from "@orderofchaos/mvvm";
import { Layout } from "@/components/Layout/Layout";
import { EmptyImage } from "@/components/EmptyImage/EmptyImage";
import { ConfirmDialog } from "@/components/ConfirmDialog/ConfirmDialog";
import type { Box } from "@/stores/Box";
import { Measures } from "@/types";
import styles from "./BoxPage.module.css";
import Button, {
  ButtonDesign,
  ButtonSize,
} from "@/components/Button/Button";
import { BoxPageViewModel } from "./BoxPageViewModel";
import { formatTypeTag, normalizeTypeTags } from "@/utils/typeTags";
import Icon, { IconSize } from "@/components/Icon/Icon";
import { ProductPhotoSlider } from "@/components/ProductPhotoSlider/ProductPhotoSlider";

interface BoxProps {
  box: Box;
}

function measureLabel(measure: Measures): string {
  if (measure === Measures.weight) {
    return "вес";
  }
  if (measure === Measures.liters) {
    return "литры";
  }
  return "шт";
}

function formatQuantity(value: number): string {
  if (!Number.isFinite(value)) {
    return "1";
  }
  return new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 3,
  }).format(value);
}

export const BoxPage: React.FC<BoxProps> = view(BoxPageViewModel)<BoxProps>(({
  viewModel: {
    box,
    childBoxes,
    childProducts,
    isLoading,
    loadError,
    loadAll,
    historyItems,
    isHistoryLoading,
    historyError,
    actionError,
    busyProductId,
    busyBoxId,
    isDeletingCurrentBox,
    isDestroyConfirmOpen,
    isDestroyConfirmProcessing,
    destroyConfirmTitle,
    destroyConfirmDescription,
    openAddProduct,
    openEditBox,
    removeProductFromBox,
    detachChildBox,
    openDeleteCurrentBoxConfirm,
    openDeleteChildBoxConfirm,
    openDeleteProductFromBoxConfirm,
    closeDestroyConfirm,
    confirmDestroy,
    openChildBox,
    openProduct,
    openBoxTypeTagFilter,
    openProductTypeTagFilter,
    openProductMeasureFilter,
    openParentBoxReference,
    openHistoryProduct,
    openHistoryBox,
  },
}) => {
  const isRefreshing = isLoading || isHistoryLoading || isDeletingCurrentBox;
  const hasPhoto = Boolean(box.photo?.trim());
  const totalItems = childBoxes.length + childProducts.length;
  const boxTypeTags = normalizeTypeTags(box.typeTags);

  return (
    <Layout title={box.name}>
      <div className={styles.page}>
        <section className={styles.heroCard}>
          <div className={styles.media}>
            {hasPhoto ? (
              <img
                src={box.photo}
                alt={box.name}
                className={styles.image}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            ) : (
              <div className={styles.emptyImage}>
                <EmptyImage />
              </div>
            )}
          </div>

          <div className={styles.heroContent}>
            <div className={styles.heroTitleRow}>
              <h2 className={styles.heroTitle}>{box.name}</h2>
              <span className={styles.heroBadge}>
                {totalItems} {totalItems === 1 ? "позиция" : "позиций"}
              </span>
            </div>

            <p className={styles.heroDescription}>
              {box.description?.trim()
                ? box.description
                : "Описание коробки пока не заполнено."}
            </p>

            <div className={styles.heroChips}>
              {boxTypeTags.length > 0 ? (
                boxTypeTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`${styles.chip} ${styles.chipButton}`}
                    onClick={() => openBoxTypeTagFilter(tag)}
                  >
                    {formatTypeTag(tag)}
                  </button>
                ))
              ) : (
                <span className={styles.chip}>без тега</span>
              )}
              <button
                type="button"
                className={`${styles.chip} ${styles.chipButton}`}
                onClick={openParentBoxReference}
              >
                родитель:{" "}
                {box.parentId ? "родительская коробка" : "верхний уровень"}
              </button>
            </div>

            <div className={styles.heroStats}>
              <article className={styles.heroStat}>
                <span>вложенных коробок</span>
                <strong>{childBoxes.length}</strong>
              </article>
              <article className={styles.heroStat}>
                <span>товаров внутри</span>
                <strong>{childProducts.length}</strong>
              </article>
              <article className={styles.heroStat}>
                <span>операций в истории</span>
                <strong>{historyItems.length}</strong>
              </article>
            </div>
          </div>
        </section>

        {loadError && <p className={styles.error}>{loadError}</p>}
        {actionError && <p className={styles.error}>{actionError}</p>}

        <section className={styles.contentsCard}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Содержимое коробки</h3>
            <div className={styles.sectionActions}>
              <Button
                design={ButtonDesign.basic}
                size={ButtonSize.tiny}
                onClick={openAddProduct}
                disabled={isRefreshing}
              >
                добавить товар
              </Button>
              <Button
                design={ButtonDesign.basic}
                size={ButtonSize.tiny}
                onClick={openEditBox}
                disabled={isRefreshing}
              >
                редактировать
              </Button>
              <Button
                design={ButtonDesign.basic}
                size={ButtonSize.tiny}
                className={styles.destroyButton}
                onClick={openDeleteCurrentBoxConfirm}
                disabled={isRefreshing || !!busyProductId || !!busyBoxId}
              >
                уничтожить коробку
              </Button>
              <Button
                design={ButtonDesign.ghost}
                size={ButtonSize.tiny}
                className={styles.refresh}
                onClick={() => void loadAll()}
                disabled={isRefreshing}
              >
                обновить
              </Button>
            </div>
          </div>
          <p className={styles.sectionCaption}>
            Управляйте вложенными коробками и товарами: открывайте карточки,
            перемещайте или удаляйте элементы.
          </p>

          {isLoading && (
            <p className={styles.helper}>Загружаем содержимое...</p>
          )}

          {!isLoading && (
            <div className={styles.contentGrid}>
              <section className={`${styles.stack} ${styles.stackBoxes}`}>
                <h4 className={styles.stackTitle}>
                  <span className={styles.stackTitleMain}>
                    <Icon name={"Bag"} size={IconSize.tiny} />
                    Вложенные коробки
                  </span>
                  <span className={styles.stackCount}>{childBoxes.length}</span>
                </h4>

                {childBoxes.length === 0 ? (
                  <p className={styles.helper}>Вложенных коробок нет</p>
                ) : (
                  <ul className={styles.entityList}>
                    {childBoxes.map((childBox) => {
                      const childBoxTags = normalizeTypeTags(childBox.typeTags);

                      return (
                        <li key={childBox.id} className={styles.entityItem}>
                          <div className={styles.entityMain}>
                            <div className={styles.entityTitleRow}>
                              <span
                                className={`${styles.entityKindBadge} ${styles.entityKindBox}`}
                              >
                                <Icon name={"Bag"} size={IconSize.tiny} />
                                коробка
                              </span>
                              <Button
                                className={styles.entityButton}
                                design={ButtonDesign.basic}
                                onClick={() => openChildBox(childBox)}
                              >
                                {childBox.name}
                              </Button>
                            </div>
                            <p className={styles.entityMeta}>
                              <span className={styles.entityMetaRow}>
                                {childBoxTags.length > 0 ? (
                                  childBoxTags.map((tag) => (
                                    <button
                                      key={`${childBox.id}-${tag}`}
                                      type="button"
                                      className={styles.metaChipButton}
                                      onClick={() => openBoxTypeTagFilter(tag)}
                                    >
                                      {formatTypeTag(tag)}
                                    </button>
                                  ))
                                ) : (
                                  <span className={styles.metaChipButton}>
                                    без тега
                                  </span>
                                )}
                              </span>
                            </p>
                          </div>

                          <div className={styles.itemActions}>
                            <Button
                              design={ButtonDesign.basic}
                              size={ButtonSize.tiny}
                              className={styles.itemActionButton}
                              onClick={() => void detachChildBox(childBox)}
                              disabled={
                                busyBoxId === childBox.id || isRefreshing
                              }
                            >
                              вынести
                            </Button>
                            <Button
                              design={ButtonDesign.basic}
                              size={ButtonSize.tiny}
                              className={`${styles.itemActionButton} ${styles.itemActionDanger}`}
                              onClick={() =>
                                openDeleteChildBoxConfirm(childBox)
                              }
                              disabled={
                                busyBoxId === childBox.id || isRefreshing
                              }
                            >
                              уничтожить
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section className={`${styles.stack} ${styles.stackProducts}`}>
                <h4 className={styles.stackTitle}>
                  <span className={styles.stackTitleMain}>
                    <Icon name={"File"} size={IconSize.tiny} />
                    Товары
                  </span>
                  <span className={styles.stackCount}>
                    {childProducts.length}
                  </span>
                </h4>

                {childProducts.length === 0 ? (
                  <p className={styles.helper}>Товаров внутри нет</p>
                ) : (
                  <ul className={styles.productGrid}>
                    {childProducts.map((product) => {
                      const productTags = normalizeTypeTags(product.typeTags);
                      const productName = product.name || "Без названия";

                      return (
                        <li key={product.id} className={styles.productCard}>
                          <button
                            type="button"
                            className={styles.productPreviewButton}
                            onClick={() => openProduct(product)}
                            aria-label={`Открыть товар ${productName}`}
                          >
                            <ProductPhotoSlider
                              photo={product.photo}
                              photos={product.photos}
                              alt={productName}
                              className={styles.productSlider}
                              trackClassName={styles.productSliderTrack}
                              imageClassName={styles.productSliderImage}
                              emptyClassName={styles.productSliderEmpty}
                            />
                          </button>

                          <div className={styles.productContent}>
                            <button
                              type="button"
                              className={styles.productTitleButton}
                              onClick={() => openProduct(product)}
                            >
                              {productName}
                            </button>
                            <span className={styles.productQty}>
                              {formatQuantity(product.quantity)}{" "}
                              {measureLabel(product.measure)}
                            </span>
                            <div className={styles.productMetaRow}>
                              <button
                                type="button"
                                className={styles.metaChipButton}
                                onClick={() =>
                                  openProductMeasureFilter(product.measure)
                                }
                              >
                                мера: {product.measure}
                              </button>
                              {productTags.length > 0 ? (
                                productTags.map((tag) => (
                                  <button
                                    key={`${product.id}-${tag}`}
                                    type="button"
                                    className={styles.metaChipButton}
                                    onClick={() =>
                                      openProductTypeTagFilter(tag)
                                    }
                                  >
                                    {formatTypeTag(tag)}
                                  </button>
                                ))
                              ) : (
                                <span className={styles.metaChipButton}>
                                  без тега
                                </span>
                              )}
                            </div>
                          </div>

                          <div className={styles.productActions}>
                            <Button
                              design={ButtonDesign.basic}
                              size={ButtonSize.tiny}
                              className={styles.itemActionButton}
                              onClick={() => void removeProductFromBox(product)}
                              disabled={
                                busyProductId === product.id || isRefreshing
                              }
                            >
                              изъять
                            </Button>
                            <Button
                              design={ButtonDesign.basic}
                              size={ButtonSize.tiny}
                              className={`${styles.itemActionButton} ${styles.itemActionDanger}`}
                              onClick={() =>
                                openDeleteProductFromBoxConfirm(product)
                              }
                              disabled={
                                busyProductId === product.id || isRefreshing
                              }
                            >
                              уничтожить
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          )}
        </section>

        <section className={styles.historyCard}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>История операций</h3>
            <Button
              design={ButtonDesign.ghost}
              size={ButtonSize.tiny}
              className={styles.refresh}
              onClick={() => void loadAll()}
              disabled={isRefreshing}
            >
              обновить
            </Button>
          </div>

          {historyError && <p className={styles.error}>{historyError}</p>}
          {isHistoryLoading && (
            <p className={styles.helper}>Загружаем историю...</p>
          )}
          {!isHistoryLoading && historyItems.length === 0 && (
            <p className={styles.helper}>Операций по коробке пока нет</p>
          )}

          {!isHistoryLoading && historyItems.length > 0 && (
            <ul className={styles.historyList}>
              {historyItems.map((item) => (
                <li
                  key={item.id}
                  className={`${styles.historyItem} ${
                    item.tone === "danger"
                      ? styles.historyItemDanger
                      : item.tone === "warning"
                        ? styles.historyItemWarning
                        : styles.historyItemInfo
                  }`}
                >
                  <div className={styles.historyHead}>
                    <p className={styles.historyAction}>{item.title}</p>
                    <div className={styles.historyHeadMeta}>
                      <span
                        className={
                          item.actorType === "system"
                            ? `${styles.historySourceBadge} ${styles.historySourceSystem}`
                            : `${styles.historySourceBadge} ${styles.historySourceUser}`
                        }
                      >
                        {item.actorType === "system"
                          ? "системное"
                          : "пользователь"}
                      </span>
                      <p className={styles.historyTime}>{item.timestamp}</p>
                    </div>
                  </div>

                  <div className={styles.historyGrid}>
                    <p className={styles.historyField}>
                      <span>Автор:</span> {item.actorLabel}
                    </p>
                    <p className={styles.historyField}>
                      <span>Товар:</span>{" "}
                      <button
                        type="button"
                        className={styles.historyLink}
                        onClick={() => void openHistoryProduct(item.productId)}
                      >
                        {item.productLabel}
                      </button>
                    </p>
                    <p className={styles.historyField}>
                      <span>Количество:</span> {item.quantityLabel}
                    </p>
                    <p className={styles.historyField}>
                      <span>Коробка:</span>{" "}
                      <button
                        type="button"
                        className={styles.historyLink}
                        onClick={() => void openHistoryBox(item.boxId)}
                      >
                        {item.boxLabel}
                      </button>
                    </p>
                    {item.comment && (
                      <p className={styles.historyComment}>
                        <span>Комментарий:</span> {item.comment}
                      </p>
                    )}
                  </div>

                  <div className={styles.historyActions}>
                    <Button
                      design={ButtonDesign.basic}
                      size={ButtonSize.tiny}
                      onClick={() => void openHistoryProduct(item.productId)}
                      disabled={
                        busyProductId === item.productId || isRefreshing
                      }
                    >
                      открыть товар
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <ConfirmDialog
        isOpen={isDestroyConfirmOpen}
        title={destroyConfirmTitle}
        description={destroyConfirmDescription}
        reasonPrompt={"Причина удаления"}
        reasonPlaceholder={"Например: повреждена, расформирована, списана"}
        reasonRequired={true}
        reasonMinLength={3}
        reasonMaxLength={280}
        confirmLabel={"уничтожить"}
        cancelLabel={"отмена"}
        isProcessing={isDestroyConfirmProcessing}
        onCancel={closeDestroyConfirm}
        onConfirm={confirmDestroy}
      />
    </Layout>
  );
});
